import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  IconButton,
  InputAdornment,
  Divider
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { DEFAULT_PROMPT } from '../constants';

const Settings = ({ settings, setSettings }) => {
  const [localSettings, setLocalSettings] = useState({ ...settings });
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
  const [showApiKey, setShowApiKey] = useState(false);

  // Sync localSettings with parent settings when they change
  useEffect(() => {
    setLocalSettings({ ...settings });
  }, [settings]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setLocalSettings({ ...localSettings, [name]: value });
  };

  const handleSaveSettings = () => {
    if (!localSettings.apiUrl) {
      showNotification('API URL is required', 'error');
      return;
    }

    if (!localSettings.model) {
      showNotification('Model name is required', 'error');
      return;
    }

    if (localSettings.temperature === '' || localSettings.temperature < 0 || localSettings.temperature > 2) {
      showNotification('Temperature must be between 0 and 2', 'error');
      return;
    }

    if (!localSettings.maxTokens || localSettings.maxTokens < 1 || localSettings.maxTokens > 16000) {
      showNotification('Max tokens must be between 1 and 16000', 'error');
      return;
    }

    if (!localSettings.prompt) {
      showNotification('Prompt is required', 'error');
      return;
    }

    setSettings(localSettings);
    showNotification('Settings saved successfully', 'success');
  };

  const handleResetPrompt = () => {
    setLocalSettings({ ...localSettings, prompt: DEFAULT_PROMPT });
  };

  const showNotification = (message, severity) => {
    setNotification({ open: true, message, severity });
  };

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  const toggleShowApiKey = () => {
    setShowApiKey(!showApiKey);
  };

  return (
    <Box>
      <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 2, backgroundColor: '#f8f9fa' }}>
        <Typography variant="body1" paragraph>
          Configure your OpenAI API settings and formatting prompt below. These settings are required for the formatter to work.
          Your API key is stored locally in your browser and is not sent to any server except OpenAI.
        </Typography>
      </Paper>

      <Grid container spacing={3}>
        {/* API Configuration Section */}
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
            API Configuration
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <TextField
            label="OpenAI API Key (Optional)"
            name="apiKey"
            value={localSettings.apiKey}
            onChange={handleChange}
            fullWidth
            margin="normal"
            type={showApiKey ? 'text' : 'password'}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={toggleShowApiKey}
                    edge="end"
                  >
                    {showApiKey ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            helperText="Your OpenAI API key. Leave empty to use the default key. Get your own at https://platform.openai.com/api-keys"
          />
        </Grid>

        {/* Compact API Settings Row */}
        <Grid item xs={12} md={4}>
          <TextField
            label="OpenAI API URL"
            name="apiUrl"
            value={localSettings.apiUrl}
            onChange={handleChange}
            fullWidth
            required
            margin="normal"
            helperText="API endpoint URL"
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <FormControl fullWidth margin="normal">
            <InputLabel id="model-select-label">OpenAI Model</InputLabel>
            <Select
              labelId="model-select-label"
              name="model"
              value={localSettings.model}
              onChange={handleChange}
              label="OpenAI Model"
            >
              <MenuItem value="gpt-4.1-mini">GPT-4.1-mini</MenuItem>
              <MenuItem value="gpt-4.1">GPT-4.1</MenuItem>
              <MenuItem value="gpt-4o-mini">GPT-4o-mini</MenuItem>
              <MenuItem value="gpt-4o-latest">GPT-4o-latest</MenuItem>
              <MenuItem value="gpt-4">GPT-4</MenuItem>
              <MenuItem value="gpt-o4-mini">GPT-o4-mini</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={2.5}>
          <TextField
            label="Temperature"
            name="temperature"
            type="number"
            value={localSettings.temperature}
            onChange={handleChange}
            fullWidth
            required
            margin="normal"
            inputProps={{ min: 0, max: 2, step: 0.1 }}
            helperText="Randomness (0-2)"
          />
        </Grid>

        <Grid item xs={12} md={2.5}>
          <TextField
            label="Max Tokens"
            name="maxTokens"
            type="number"
            value={localSettings.maxTokens}
            onChange={handleChange}
            fullWidth
            required
            margin="normal"
            inputProps={{ min: 1, max: 16000, step: 1 }}
            helperText="Response limit (1-16000)"
          />
        </Grid>

        {/* Divider */}
        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} />
        </Grid>

        {/* Formatting Prompt Section - Now with much more space */}
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
            Formatting Prompt
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            This prompt instructs the AI on how to format the scholarship content into Markdown. Modify it to customize the output style and requirements.
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <TextField
            label="Customize the formatting prompt"
            name="prompt"
            multiline
            rows={16}
            value={localSettings.prompt || DEFAULT_PROMPT}
            onChange={handleChange}
            fullWidth
            variant="outlined"
            margin="normal"
            sx={{
              '& .MuiInputBase-input': {
                fontFamily: 'monospace',
                fontSize: '0.9rem',
                lineHeight: 1.4
              }
            }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
            <Button 
              variant="outlined" 
              onClick={handleResetPrompt}
              size="small"
            >
              Reset to Default
            </Button>
            <Typography variant="caption" color="text.secondary">
              Characters: {(localSettings.prompt || DEFAULT_PROMPT).length}
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              onClick={handleSaveSettings}
              size="large"
              sx={{ minWidth: 200 }}
            >
              Save Settings
            </Button>
          </Box>
        </Grid>
      </Grid>

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
      >
        <Alert onClose={handleCloseNotification} severity={notification.severity}>
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Settings; 